<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="
            serviceType === contractServiceType.ownCruise || serviceType === contractServiceType.tour
                ? $t('product.info.create_accommodation')
                : $t('product.info.create_service')
        "
        :edit-mode-text="getEditModeText()"
        :edit-mode-only="isHotelDescriptionMode"
        @reset="reset()"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table-server
                :headers="useHeaders"
                :items="useFilteredServices"
                :items-per-page="-1"
                :items-per-page-options="[-1]"
                :loading="isLoading"
                :items-length="useFilteredServices.length"
                disable-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                :class="{'table-server-sorting': isOwnHotel}"
                data-cy="guests-table"
            >
                <template v-slot:item.name="{item}">
                    <div class="d-flex align-center">
                        <!-- Tree branch for ZERO/SUSTAINABLE rooms -->
                        <div
                            v-if="isOwnHotel && item.sustainabilityLevel !== 'BASIC' && !orphanRoomIds.has(item.id)"
                            class="me-2"
                        >
                            <v-icon size="16" color="grey">mdi-subdirectory-arrow-right</v-icon>
                        </div>
                        <div
                            class="text-link"
                            :class="{
                                'ms-4':
                                    isOwnHotel && item.sustainabilityLevel !== 'BASIC' && !orphanRoomIds.has(item.id),
                            }"
                            @click="editService(item)"
                        >
                            {{ localized(item.name) }}
                            <span
                                v-if="isOwnHotel && item.sustainabilityLevel === 'ZERO'"
                                class="text-grey-darken-1 ms-1"
                                >(zero)</span
                            >
                            <span
                                v-if="isOwnHotel && item.sustainabilityLevel === 'SUSTAINABLE'"
                                class="text-grey-darken-1 ms-1"
                                >(sustainable)</span
                            >
                        </div>
                    </div>
                </template>
                <template v-slot:item.category="{item}">
                    {{ item.categoryId && useCatalogue?.find(({id}) => id == item.categoryId)?.name }}
                </template>
                <template v-slot:item.roomCategoryId="{item}">
                    {{ item.roomCategoryId && categories?.find(({id}) => id == item.roomCategoryId)?.name }}
                </template>
                <template v-slot:item.roomType="{item}">
                    {{ item.roomType }}
                </template>
                <template v-slot:item.active="{item}">
                    {{ item.active ? $t('active') : $t('inactive') }}
                </template>
                <template v-slot:item.actions="{item}">
                    <div
                        v-if="isOwnHotel && item.sustainabilityLevel === 'BASIC'"
                        class="d-flex align-center"
                        style="gap: 8px"
                    >
                        <!-- Create Zero Button -->
                        <template v-if="!hasZeroSubtype(item)">
                            <v-tooltip
                                :text="canCreateZeroRoom(item) ? '' : $t('tooltip.need_amenities_for_zero')"
                                :disabled="canCreateZeroRoom(item)"
                            >
                                <template v-slot:activator="{props}">
                                    <v-btn
                                        v-bind="props"
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        :class="{'btn-custom-disabled': !canCreateZeroRoom(item)}"
                                        @click="handleZeroRoomClick(item, $event)"
                                    >
                                        {{ $t('create_zero') }}
                                    </v-btn>
                                </template>
                            </v-tooltip>
                        </template>

                        <!-- Create Sustainable Button -->
                        <template v-if="!hasSustainableSubtype(item)">
                            <v-tooltip
                                :text="
                                    canCreateSustainableRoom(item)
                                        ? ''
                                        : $t('tooltip.need_eco_amenities_for_sustainable')
                                "
                                :disabled="canCreateSustainableRoom(item)"
                            >
                                <template v-slot:activator="{props}">
                                    <v-btn
                                        v-bind="props"
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        :class="{'btn-custom-disabled': !canCreateSustainableRoom(item)}"
                                        @click="handleSustainableRoomClick(item, $event)"
                                    >
                                        {{ $t('create_sustainable') }}
                                    </v-btn>
                                </template>
                            </v-tooltip>
                        </template>
                    </div>
                </template>
            </v-data-table-server>
        </template>
        <template v-slot:additional-mode>
            <template v-if="!isOwnHotel">
                <InfoServiceForm
                    :edit-service-id="editServiceId"
                    :contract-id="contractId"
                    :service-type="serviceType"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
            <template v-if="isOwnHotel">
                <RoomTypesForm
                    :service="editableService"
                    :categories="categories"
                    :bed-types="bedTypes"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import InfoServiceForm from '~/components/products/information/InfoServiceForm.vue'
    import {contractServiceType} from '~/utils/types/types'
    import RoomTypesForm from '~src/components/products/information/RoomTypesForm.vue'
    import {useCatalogue, useHotelInfo} from '~/composables/form-helpers'
    import {CHANGE_LOCALE_EVENT, EventBus} from '~/utils/event-bus'
    import {clone} from '~/utils/helpers'

    const localized = useLocalized()

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId?: number
        serviceType: string
    }>()

    const emit = defineEmits(['edit'])

    const isLoading = ref<boolean>(false)
    const modeSwitcher = ref(null)
    const editServiceId = ref<number>(null)
    const editableService = ref(null)
    const services = ref<any[]>([])
    const isOwnHotel = ref<boolean>(props.serviceType === contractServiceType.ownHotel)
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const bedTypes = ref([])
    const categories = ref([])
    const activeFilter = ref<boolean>(true)
    const roomAmenities = ref<Record<number, any[]>>({})

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (isOwnHotel.value) {
            await loadRoomTypeData()
            await loadHotelRoomDictionaryInfo()
            EventBus.$on(CHANGE_LOCALE_EVENT, loadHotelRoomDictionaryInfo)
        } else await loadData()
        if (!isOwnHotel.value && !isHotelDescriptionMode && !useCatalogue.value?.length) {
            await supplierStore.loadCatalogue()
        }
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, loadHotelRoomDictionaryInfo)
    })

    watch(useHotelInfo, async () => {
        if (isOwnHotel.value) {
            await loadRoomTypeData()
            modeSwitcher.value.goToTable()
        }
    })

    async function loadHotelRoomDictionaryInfo(): Promise<void> {
        try {
            const res = await app.$api.hotelRoomDictionaryInfo.get({supplierId: 1})
            bedTypes.value = res.bedTypes.sort((a, b) => a.name.localeCompare(b.name))
            categories.value = res.categories.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            bedTypes.value = []
            categories.value = []
        }
    }

    async function loadRoomTypeData(): Promise<void> {
        try {
            isLoading.value = true

            const {hotelRoomTypes} = await app.$api.hotelDescriptionRoomType.get(supplierStore.hotelInfo.hotelId, {
                locale: 'all',
            })

            console.log(`Loaded ${hotelRoomTypes?.length || 0} room types`)

            // Process room types to ensure they have the required sustainability level
            const processedRoomTypes = hotelRoomTypes.map(roomType => ({
                ...roomType,
                sustainabilityLevel: roomType.sustainabilityLevel || 'BASIC',
                basicRoomId: roomType.basicRoomId || null,
            }))

            services.value = processedRoomTypes

            // Load amenities for each basic room type to enable validation
            await loadRoomAmenities()
        } catch (e) {
            console.error('Error loading room types:', e)
            services.value = []
        } finally {
            isLoading.value = false
        }
    }

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {contractId: props.contractId}
            const res = await app.$api.ownProductServices.get(rq)
            services.value = res.services
        } catch (e) {
            services.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        if (isOwnHotel.value) {
            await loadRoomTypeData()
            // Reload amenities after saving to update button states
            await loadRoomAmenities()
        } else await loadData()
        modeSwitcher.value.goToTable()
    }

    const reset = () => {
        editServiceId.value = null
        editableService.value = null
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        if (isOwnHotel.value) {
            return [
                {title: t('name'), value: 'name', minWidth: '120px'},
                {title: t('category'), value: 'roomCategoryId'},
                {title: t('room_type'), value: 'roomType', width: '140px'},
                {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
                {title: t('actions'), value: 'actions', width: '200px'},
            ]
        }
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('category'), value: 'category'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
    })

    const editService = (servise): void => {
        modeSwitcher.value.edit()
        editServiceId.value = servise?.id
        if (isOwnHotel.value) editableService.value = servise
    }

    const useFilteredServices = computed(() => {
        const filtered = services.value.filter(({active}) => activeFilter.value === active)

        if (!isOwnHotel.value) {
            return filtered
        }

        // Organize room types in tree structure for hotels
        const organized = []
        const basicRooms = filtered.filter(room => room.sustainabilityLevel === 'BASIC')
        const specialRooms = filtered.filter(room => room.sustainabilityLevel !== 'BASIC')

        // Add each basic room followed by its descendants
        basicRooms.forEach(basicRoom => {
            organized.push(basicRoom)

            // Find and add ZERO/SUSTAINABLE rooms that belong to this basic room
            const descendants = specialRooms.filter(room => room.basicRoomId === basicRoom.id)
            descendants.forEach(descendant => {
                organized.push(descendant)
            })
        })

        // Add any orphaned special rooms (those without a valid basicRoomId)
        const orphanedSpecialRooms = specialRooms.filter(
            room => !basicRooms.some(basicRoom => basicRoom.id === room.basicRoomId)
        )
        orphanedSpecialRooms.forEach(orphan => {
            organized.push(orphan)
        })

        return organized
    })

    // ZSH-873: ids of special rooms whose BASIC parent isn't in the current active/inactive view
    // (orphans). The template renders them top-level (non-indented) so they don't nest under the
    // wrong basic. Kept as a separate Set so it never lands on the item and pollutes the save payload.
    const orphanRoomIds = computed(() => {
        if (!isOwnHotel.value) return new Set()
        const filtered = services.value.filter(({active}) => activeFilter.value === active)
        const basicRooms = filtered.filter(room => room.sustainabilityLevel === 'BASIC')
        const specialRooms = filtered.filter(room => room.sustainabilityLevel !== 'BASIC')
        return new Set(
            specialRooms
                .filter(room => !basicRooms.some(basicRoom => basicRoom.id === room.basicRoomId))
                .map(room => room.id)
        )
    })

    const getEditModeText = () => {
        try {
            const serviceType = props.serviceType

            if (!serviceType) {
                return 'Edit'
            }

            const baseText =
                serviceType === contractServiceType.ownCruise || serviceType === contractServiceType.tour
                    ? t('product.info.edit_accommodation')
                    : t('product.info.edit_service')

            // Add room name and sustainability type after dash if we're editing a room and have room data
            if (editableService.value?.name) {
                const roomName = localized(editableService.value.name)
                if (roomName) {
                    let displayText = `${baseText} - ${roomName}`

                    // Add sustainability type if it's not BASIC
                    if (
                        editableService.value.sustainabilityLevel &&
                        editableService.value.sustainabilityLevel !== 'BASIC'
                    ) {
                        const sustainabilityType = editableService.value.sustainabilityLevel.toLowerCase()
                        displayText += ` ${sustainabilityType}`
                    }

                    return displayText
                }
            }

            return baseText
        } catch (error) {
            console.warn('Error getting edit mode text:', error)
            return 'Edit'
        }
    }

    const createZeroRoom = async basicRoom => {
        console.log('Create zero room for:', basicRoom)

        // Check if a sustainable room already exists for this basic room
        const existingSustainableRoom = findExistingSustainableRoom(basicRoom)
        let existingAmenityPrices = []

        // Only load prices if a sustainable room variant already exists
        // BASIC rooms don't have amenity prices, so we only reuse from sustainable/zero variants
        if (existingSustainableRoom) {
            console.log('Found existing sustainable room, loading amenity prices:', existingSustainableRoom.id)
            existingAmenityPrices = await loadAmenityPricesFromVariant(existingSustainableRoom.id)
        } else {
            console.log('No existing sustainable room found, no price pre-population will occur')
        }

        // Create a copy of the basic room data and modify it for ZERO room
        const zeroRoomData = {
            ...clone(basicRoom),
            // Remove the ID so it's treated as a new room
            id: null,
            // Add the required fields for special room types
            sustainabilityLevel: 'ZERO',
            basicRoomId: basicRoom.id,
            // Update the description with additional text
            description: basicRoom.description
                ? {
                      ...basicRoom.description,
                      en: `${basicRoom.description?.en || (typeof basicRoom.description === 'string' ? basicRoom.description : '') || ''} ${t('room_descriptions.eco_friendly_description_en')}`,
                      el: `${basicRoom.description?.el || (typeof basicRoom.description === 'string' ? basicRoom.description : '') || ''} ${t('room_descriptions.eco_friendly_description_el')}`,
                  }
                : {
                      en: t('room_descriptions.eco_friendly_description_en'),
                      el: t('room_descriptions.eco_friendly_description_el'),
                  },
        }

        // Only add existing amenity prices if we found a sustainable variant
        if (existingAmenityPrices.length > 0) {
            zeroRoomData.existingAmenityPrices = existingAmenityPrices
        }

        // Set the editable service and switch to edit mode
        editableService.value = zeroRoomData
        modeSwitcher.value.edit()
    }

    const createSustainableRoom = async basicRoom => {
        console.log('Create sustainable room for:', basicRoom)

        // Check if a zero room already exists for this basic room
        const existingZeroRoom = findExistingZeroRoom(basicRoom)
        let existingAmenityPrices = []

        // Only load prices if a zero room variant already exists
        // BASIC rooms don't have amenity prices, so we only reuse from sustainable/zero variants
        if (existingZeroRoom) {
            console.log('Found existing zero room, loading amenity prices:', existingZeroRoom.id)
            existingAmenityPrices = await loadAmenityPricesFromVariant(existingZeroRoom.id)
        } else {
            console.log('No existing zero room found, no price pre-population will occur')
        }

        // Create a copy of the basic room data and modify it for SUSTAINABLE room
        const sustainableRoomData = {
            ...clone(basicRoom),
            // Remove the ID so it's treated as a new room
            id: null,
            // Add the required fields for special room types
            sustainabilityLevel: 'SUSTAINABLE',
            basicRoomId: basicRoom.id,
            // Update the description with additional text
            description: basicRoom.description
                ? {
                      ...basicRoom.description,
                      en: `${basicRoom.description?.en || (typeof basicRoom.description === 'string' ? basicRoom.description : '') || ''} ${t('room_descriptions.eco_friendly_description_en')}`,
                      el: `${basicRoom.description?.el || (typeof basicRoom.description === 'string' ? basicRoom.description : '') || ''} ${t('room_descriptions.eco_friendly_description_el')}`,
                  }
                : {
                      en: t('room_descriptions.eco_friendly_description_en'),
                      el: t('room_descriptions.eco_friendly_description_el'),
                  },
        }

        // Only add existing amenity prices if we found a zero variant
        if (existingAmenityPrices.length > 0) {
            sustainableRoomData.existingAmenityPrices = existingAmenityPrices
        }

        // Set the editable service and switch to edit mode
        editableService.value = sustainableRoomData
        modeSwitcher.value.edit()
    }

    // Helper functions to check if subtypes already exist
    const hasZeroSubtype = basicRoom => {
        return services.value.some(room => room.basicRoomId === basicRoom.id && room.sustainabilityLevel === 'ZERO')
    }

    const hasSustainableSubtype = basicRoom => {
        return services.value.some(
            room => room.basicRoomId === basicRoom.id && room.sustainabilityLevel === 'SUSTAINABLE'
        )
    }

    // Helper functions to find existing room type variants
    const findExistingZeroRoom = basicRoom => {
        return services.value.find(room => room.basicRoomId === basicRoom.id && room.sustainabilityLevel === 'ZERO')
    }

    const findExistingSustainableRoom = basicRoom => {
        return services.value.find(
            room => room.basicRoomId === basicRoom.id && room.sustainabilityLevel === 'SUSTAINABLE'
        )
    }

    // Function to load amenities for all basic room types
    const loadRoomAmenities = async () => {
        try {
            const basicRooms = services.value.filter(room => room.sustainabilityLevel === 'BASIC')

            for (const room of basicRooms) {
                try {
                    const amenitiesResponse = await app.$api.hotelRoomAmenities.get({
                        productId: supplierStore.fullContract.id,
                        roomId: room.id,
                        locale: 'all',
                    })
                    roomAmenities.value[room.id] = amenitiesResponse || []
                } catch (error) {
                    console.error(`Error loading amenities for room ${room.id}:`, error)
                    roomAmenities.value[room.id] = []
                }
            }
        } catch (error) {
            console.error('Error loading room amenities:', error)
        }
    }

    // Function to load amenity prices from an existing room type variant
    const loadAmenityPricesFromVariant = async roomId => {
        try {
            const amenitiesResponse = await app.$api.hotelRoomAmenities.get({
                productId: supplierStore.fullContract.id,
                roomId: roomId,
                locale: 'all',
            })
            return amenitiesResponse || []
        } catch (error) {
            console.error(`Error loading amenity prices from room ${roomId}:`, error)
            return []
        }
    }

    // Helper functions to check if a basic room has enough amenities to create subtypes
    const canCreateSustainableRoom = basicRoom => {
        const amenities = roomAmenities.value[basicRoom.id] || []
        const ecoAmenities = amenities.filter(
            amenity => amenity.category === 'ECO_AMENITIES' && amenity.enabled === true
        )
        return ecoAmenities.length >= 2
    }

    const canCreateZeroRoom = basicRoom => {
        const amenities = roomAmenities.value[basicRoom.id] || []
        const ecoAmenities = amenities.filter(
            amenity => amenity.category === 'ECO_AMENITIES' && amenity.enabled === true
        )
        const totalEnabledAmenities = amenities.filter(amenity => amenity.enabled === true)
        return ecoAmenities.length >= 1 && totalEnabledAmenities.length >= 2
    }

    // Click handlers that prevent action when requirements aren't met
    const handleZeroRoomClick = async (item, event) => {
        if (!canCreateZeroRoom(item)) {
            event.preventDefault()
            event.stopPropagation()
            return false
        }
        await createZeroRoom(item)
    }

    const handleSustainableRoomClick = async (item, event) => {
        if (!canCreateSustainableRoom(item)) {
            event.preventDefault()
            event.stopPropagation()
            return false
        }
        await createSustainableRoom(item)
    }
</script>

<style scoped>
    .btn-custom-disabled {
        opacity: 0.4 !important;
        cursor: not-allowed !important;
    }

    .btn-custom-disabled:hover {
        opacity: 0.4 !important;
    }
</style>
